import React from 'react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Eye, ShoppingCart } from 'lucide-react';

export const ProductCard = ({ product, onViewBlend }) => {
  const { addToCart } = useCart();

  const colorMap = {
    'herb-savory': '#2D5016',
    'citrus-garlic': '#D4540A',
    'smoky-savory': '#5C3D2E',
    'seafood-seasoning': '#1E4D6B',
    'full-set': '#2A2A2A',
  };

  const accentColor = colorMap[product.id] || '#D4540A';
  const isBundle = product.is_bundle || product.id === 'full-set';

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(product, 1);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div
      className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-[#E5E0D8]"
      data-testid={`product-card-${product.id}`}
    >
      {/* Product Image - Bundle shows official 4-bottle image, single shows 1 bottle */}
      <div className="relative aspect-square overflow-hidden bg-[#3D2B1F]">
        {isBundle ? (
          <div className="w-full h-full p-2">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-contain"
              data-testid={`product-image-${product.id}`}
            />
            {/* Best Value badge */}
            <div className="absolute top-3 left-3 bg-[#D4540A] text-white px-2 py-1 rounded text-xs font-bold shadow-lg z-10">
              BEST VALUE
            </div>
            {/* 4-Pack badge */}
            <div className="absolute bottom-3 right-3 bg-[#2A2A2A] text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg z-10">
              4-PACK SET
            </div>
          </div>
        ) : (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            data-testid={`product-image-${product.id}`}
          />
        )}
        <div
          className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-sm font-bold text-white shadow-lg z-10"
          style={{ backgroundColor: accentColor }}
        >
          ${product.price.toFixed(2)}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        <div>
          <h3 className="font-['Bodoni_Moda'] text-xl font-bold text-[#2A2A2A]" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>
          <p className="text-sm font-medium" style={{ color: accentColor }}>
            {product.subtitle}
          </p>
        </div>

        <p className="text-sm text-[#666] line-clamp-2 min-h-[40px]">
          {product.hook}
        </p>

        {/* Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1 border-[#E5E0D8] hover:bg-[#F5F3F0] text-sm h-11"
            onClick={() => onViewBlend(product)}
            data-testid={`view-blend-${product.id}`}
          >
            <Eye className="w-4 h-4 mr-1.5" />
            {isBundle ? 'View Set' : 'View Blend'}
          </Button>
          <Button
            className="flex-1 text-sm text-white h-11"
            style={{ backgroundColor: accentColor }}
            onClick={handleAddToCart}
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-1.5" />
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};
